<script setup lang="ts">
/* =============================================================
   DOCKING LOCATIONS — THE OWNER'S BERTHS
   =============================================================
   Every berth the signed-in company owns, as a list beside a
   map of the same rows. A row (or a pin) opens the berth's own
   page, where it is edited, priced and blocked.

   The list is filtered by company_id here rather than trusting
   RLS to narrow it: berths are publicly readable so that ship
   operators can search them, and "mine" is a question only the
   page knows to ask.

   The starting price is each berth's first rate rule -- the one
   a short stay by a small ship usually hits. It is a hint for
   the glance, fetched per berth and allowed to fail quietly; the
   Rates tab is where the card is read properly.
   ============================================================= */

import { computed, defineAsyncComponent, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import PageHeader from '@/components/ui/PageHeader.vue'
import Panel from '@/components/ui/Panel.vue'
import Button from '@/components/ui/Button.vue'
import Alert from '@/components/ui/Alert.vue'
import Badge from '@/components/ui/Badge.vue'
import EmptyState from '@/components/ui/EmptyState.vue'
import ItemList from '@/components/ui/ItemList.vue'
import ItemRow from '@/components/ui/ItemRow.vue'
import type { MapPoint } from '@/lib/map'
import { routes, to } from '@/config/routes'
import { listBerths } from '@/lib/berths'
import { listRates } from '@/lib/rates'
import { useCountries } from '@/lib/countries'
import { useAmenities } from '@/lib/amenities'
import { useAuth } from '@/lib/auth/useAuth'
import { bookingModeLabel, ratePriceText } from '@/lib/bookingLabels'
import { errorMessage } from '@/lib/errors'
import { useDocumentTitle } from '@/lib/composables/useDocumentTitle'
import type { Berth, BerthRate } from '@/lib/types'

/* Leaflet arrives in its own chunk; the list does not wait for it. */
const MapView = defineAsyncComponent(() => import('@/components/MapView.vue'))

useDocumentTitle('Docking locations')

const router = useRouter()
const { user } = useAuth()
const { countryName } = useCountries()
const { amenityLabel } = useAmenities()

const berths = ref<Berth[]>([])
/** Berth id → its first rule. Missing while loading, or when it has none. */
const firstRate = ref<Record<number, BerthRate | undefined>>({})
const loading = ref(true)
const loadError = ref('')

async function load(): Promise<void> {
  const companyId = user.value?.companyId
  if (companyId === undefined) return
  loading.value = true
  loadError.value = ''
  try {
    berths.value = await listBerths(companyId)
    void loadPrices()
  } catch (e) {
    loadError.value = errorMessage(e, 'Could not load your locations.')
  } finally {
    loading.value = false
  }
}

async function loadPrices(): Promise<void> {
  const pairs = await Promise.all(
    berths.value.map(async (b) => {
      try {
        return [b.id, (await listRates(b.id))[0]] as const
      } catch {
        return [b.id, undefined] as const
      }
    }),
  )
  firstRate.value = Object.fromEntries(pairs)
}

onMounted(load)

const countLabel = computed(() => {
  const n = berths.value.length
  return `${n} ${n === 1 ? 'location' : 'locations'}`
})

function limitsText(b: Berth): string {
  const parts = [
    b.max_length_m !== null ? `up to ${b.max_length_m} m` : null,
    b.max_vessels !== null ? `${b.max_vessels} ${b.max_vessels === 1 ? 'vessel' : 'vessels'} at once` : null,
  ].filter(Boolean)
  return parts.join(' · ')
}

const points = computed<MapPoint[]>(() =>
  berths.value.map((b) => ({
    id: b.id,
    lat: b.latitude,
    lng: b.longitude,
    title: b.name,
    subtitle: b.city,
  })),
)

function openPoint(point: MapPoint): void {
  void router.push(to.berth(Number(point.id)))
}
</script>

<template>
  <PageHeader title="Docking locations" description="The berths your company offers on River Atlas.">
    <template #actions>
      <Button icon="plus" :to="routes.locationNew">Add location</Button>
    </template>
  </PageHeader>

  <Alert v-if="loadError" tone="danger" class="page-alert">{{ loadError }}</Alert>

  <p v-if="loading && berths.length === 0" class="page-status">Loading…</p>

  <Panel v-else-if="berths.length === 0 && !loadError">
    <EmptyState
      icon="anchor"
      title="No locations yet"
      description="Add your first berth: where it is, which ships fit, and how it is booked."
    >
      <template #action>
        <Button icon="plus" :to="routes.locationNew">Add location</Button>
      </template>
    </EmptyState>
  </Panel>

  <div v-else-if="berths.length > 0" class="layout">
    <Panel flush :title="countLabel" class="listPanel">
      <ItemList>
        <ItemRow v-for="b in berths" :key="b.id" :to="to.berth(b.id)">
          <template #title>{{ b.name }}</template>
          <template #aside>
            <Badge :tone="b.booking_mode === 'instant' ? 'success' : 'warning'">
              {{ bookingModeLabel(b.booking_mode) }}
            </Badge>
          </template>
          <span>
            {{ b.city }}, {{ countryName(b.country_code) }}<template v-if="limitsText(b)"> · {{ limitsText(b) }}</template>
          </span>
          <span v-if="firstRate[b.id]" class="price">
            From {{ ratePriceText(firstRate[b.id] as BerthRate) }}
          </span>
          <span v-if="b.amenities.length > 0" class="tags">
            <Badge v-for="a in b.amenities" :key="a">{{ amenityLabel(a) }}</Badge>
          </span>
        </ItemRow>
      </ItemList>
    </Panel>

    <div class="mapWrap">
      <MapView :points="points" height="100%" @select="openPoint" />
    </div>
  </div>
</template>

<style scoped>
.layout {
  display: grid;
  gap: var(--space-md);
  grid-template-columns: minmax(0, 1fr);
}

/* On a phone the map comes first and short: where the berths are, at a
   glance, with the list right under it. */
.mapWrap {
  order: -1;
  height: 15rem;
  border: var(--border-width) solid var(--color-border);
  border-radius: var(--radius-md);
  overflow: hidden;
}

.price {
  font-weight: var(--weight-semibold);
  color: var(--color-heading);
}

.tags {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-3xs);
  margin-top: var(--space-3xs);
}

@media (min-width: 64rem) {
  .layout {
    grid-template-columns: minmax(0, 5fr) minmax(0, 4fr);
    align-items: start;
  }

  .mapWrap {
    order: 0;
    position: sticky;
    top: var(--space-md);
    height: 30rem;
  }
}
</style>
